import { yupResolver } from '@hookform/resolvers/yup'
import React, { forwardRef, useCallback, useImperativeHandle } from 'react'
import { Controller, useForm } from 'react-hook-form'
import * as yup from 'yup'
import { Box, Dialog, DialogContent, DialogTitle, TextField } from '@mui/material'
import { ActionButton } from '@emerald-works-nova/components'
import { useConfirm } from 'material-ui-confirm'

const sectionFormSchema = yup.object().shape({
  name: yup.string().required('Name is required')
})

const SectionFormDialog = ({
  section,
  onSubmit,
  isLoading,
  title,
  submitText = 'Save'
}, ref) => {
  const form = useForm({
    resolver: yupResolver(sectionFormSchema),
    mode: 'all',
    defaultValues: {
      ...section
    }
  })

  const confirm = useConfirm()

  const [open, setOpen] = React.useState(false)

  const onCancel = useCallback(() => {
    if (!form.formState.isDirty) {
      setOpen(false)
      return
    }

    confirm({
      title: 'Discard changes',
      description: 'Are you sure you want to discard the changes?',
      confirmationText: 'Yes, discard',
      cancellationText: 'No, thanks',
      confirmationButtonProps: {
        id: 'section-form-discard-confirmation-button',
        'data-test': 'section-form-discard-confirmation-button'
      },
      cancellationButtonProps: {
        id: 'section-form-discard-cancellation-button',
        'data-test': 'section-form-discard-cancellation-button'
      },
      dialogProps: {
        PaperProps: {
          id: 'section-form-discard-confirmation-modal',
          'data-test': 'section-form-discard-confirmation-modal'
        }
      },
      titleProps: {
        id: 'section-form-discard-confirmation-title',
        'data-test': 'section-form-discard-confirmation-title'
      }
    }).then(() => {
      setOpen(false)
    }).catch(() => { })
  }, [confirm, form.formState.isDirty])

  const onClose = useCallback(() => {
    setOpen(false)
  }, [setOpen])

  useImperativeHandle(ref, () => ({
    open: (formDefaultValues = { name: '' }) => {
      form.reset(formDefaultValues)
      setOpen(true)
    },
    close: () => onClose()
  }), [form, onClose])

  return (
    <Dialog
      open={open}
      // Stop propagation to avoid closing the outter accordion
      onClick={(evt) => evt.stopPropagation()}
      onFocus={(evt) => evt.stopPropagation()}
      PaperProps={{
        sx: {
          minWidth: '900px'
        }
      }}
    >
      <form onSubmit={form.handleSubmit(onSubmit)}>
        <DialogTitle>
          <Box display='flex' flexDirection='row' justifyItems='center' justifyContent='space-between'>
            {title}
            <Box display='flex' flexDirection='row' gap={2}>
              <ActionButton
                disabled={isLoading}
                onClick={onCancel}
              >
                Cancel
              </ActionButton>
              <ActionButton
                type='submit'
                isWorking={isLoading}
                variant='primary'
                disabled={!form.formState.isValid}
              >
                {submitText}
              </ActionButton>
            </Box>
          </Box>
        </DialogTitle>
        <DialogContent>
          <Box pt={2} pb={2}>
            <Controller
              name='name'
              control={form.control}
              defaultValue={section?.name || ''}
              render={({ field, fieldState }) => (
                <TextField
                  {...field}
                  sx={{ width: '100%' }}
                  error={!!fieldState.error}
                  helperText={fieldState.error?.message}
                  label='Section Name'
                />
              )}
            />
          </Box>
        </DialogContent>
      </form>
    </Dialog>
  )
}

const SectionFormDialogWithRef = forwardRef(SectionFormDialog)
export default SectionFormDialogWithRef
